import { useState, useRef, useEffect, useCallback } from 'react';
import { api } from '../api';
import '../styles/ai-designer.css';

interface AiMessage {
  role: 'user' | 'assistant';
  content: string;
}

function CodeBlock({ code, language }: { code: string; language: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="ai-designer__code-block">
      <div className="ai-designer__code-header">
        <span>{language || 'code'}</span>
        <button className="ai-designer__copy-btn" onClick={handleCopy} type="button">
          <span className="material-icons" style={{ fontSize: '14px' }}>
            {copied ? 'check' : 'content_copy'}
          </span>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="ai-designer__code-content">{code}</div>
    </div>
  );
}

function renderMessageContent(content: string) {
  const parts: Array<{ type: 'text' | 'code'; content: string; language?: string }> = [];
  const codeBlockRegex = /```(\w*)\n?([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;

  while ((match = codeBlockRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: content.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'code', content: match[2].trim(), language: match[1] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push({ type: 'text', content: content.slice(lastIndex) });
  }

  return parts.map((part, i) => {
    if (part.type === 'code') {
      return <CodeBlock key={i} code={part.content} language={part.language || ''} />;
    }
    return <span key={i}>{part.content}</span>;
  });
}

export function AIDesignerPage() {
  const [messages, setMessages] = useState<AiMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [requiresUserAuth, setRequiresUserAuth] = useState(false);
  const [geminiConnected, setGeminiConnected] = useState(false);
  const [geminiEmail, setGeminiEmail] = useState<string | null>(null);
  const [connectingGemini, setConnectingGemini] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent, scrollToBottom]);

  useEffect(() => {
    void (async () => {
      try {
        const config = await api.getAiConfig();
        setRequiresUserAuth(config.requiresUserAuth);
        if (config.requiresUserAuth) {
          const status = await api.getGeminiStatus();
          setGeminiConnected(status.connected);
          setGeminiEmail(status.email);
        }
      } catch {
        // non-fatal — chat still usable in ollama/adc modes
      }
    })();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('gemini');
    if (!result) return;
    if (result === 'connected') {
      setError(null);
      void api.getGeminiStatus().then((status) => {
        setGeminiConnected(status.connected);
        setGeminiEmail(status.email);
      });
    } else if (result === 'error') {
      const reason = params.get('reason') || 'unknown';
      setError(`Gemini connection failed: ${reason}`);
    }
    params.delete('gemini');
    params.delete('reason');
    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '');
    window.history.replaceState({}, '', newUrl);
  }, []);

  const handleConnectGemini = async () => {
    setConnectingGemini(true);
    setError(null);
    try {
      const { url } = await api.getGeminiOAuthUrl();
      window.location.href = url;
    } catch (err) {
      setConnectingGemini(false);
      setError(err instanceof Error ? err.message : 'Failed to start Gemini connection');
    }
  };

  const handleDisconnectGemini = async () => {
    try {
      await api.disconnectGemini();
      setGeminiConnected(false);
      setGeminiEmail(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect Gemini');
    }
  };

  const chatBlocked = requiresUserAuth && !geminiConnected;

  const autoResize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  };

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || streaming || chatBlocked) return;

    setError(null);
    const userMessage: AiMessage = { role: 'user', content: trimmed };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setStreaming(true);
    setStreamingContent('');

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    void api.aiChat(
      updatedMessages,
      (chunk) => {
        setStreamingContent((prev) => prev + chunk);
      },
      () => {
        setStreamingContent((prev) => {
          if (prev) {
            setMessages((msgs) => [...msgs, { role: 'assistant', content: prev }]);
          }
          return '';
        });
        setStreaming(false);
      },
      (err) => {
        setError(err.message);
        setStreamingContent((prev) => {
          if (prev) {
            setMessages((msgs) => [...msgs, { role: 'assistant', content: prev }]);
          }
          return '';
        });
        setStreaming(false);
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewConversation = () => {
    setMessages([]);
    setStreamingContent('');
    setError(null);
    setStreaming(false);
  };

  return (
    <div className="ai-designer">
      <div className="ai-designer__header">
        <span className="ai-designer__title">AI Flow Designer</span>
        {requiresUserAuth && geminiConnected && (
          <span className="ai-designer__connection" style={{ fontSize: '12px', color: '#888', marginLeft: '12px' }}>
            Connected as {geminiEmail || 'unknown'}
            <button
              onClick={handleDisconnectGemini}
              type="button"
              style={{ marginLeft: '8px', background: 'none', border: 'none', color: '#888', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Disconnect
            </button>
          </span>
        )}
        {messages.length > 0 && (
          <button
            className="ai-designer__new-btn"
            onClick={handleNewConversation}
            type="button"
          >
            <span className="material-icons" style={{ fontSize: '16px' }}>add</span>
            New chat
          </button>
        )}
      </div>

      {chatBlocked && (
        <div className="ai-designer__connect-banner" style={{ padding: '24px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
          <p style={{ marginBottom: '12px' }}>Connect your Google account to use the AI Designer.</p>
          <button
            onClick={handleConnectGemini}
            type="button"
            disabled={connectingGemini}
            className="ai-designer__new-btn"
          >
            <span className="material-icons" style={{ fontSize: '16px' }}>login</span>
            {connectingGemini ? 'Redirecting...' : 'Connect to Gemini'}
          </button>
        </div>
      )}

      <div className="ai-designer__messages">
        {messages.length === 0 && !streaming && (
          <div className="ai-designer__empty">
            <span className="material-icons">auto_awesome</span>
            <p>Describe the IO flow you want to create and the AI will generate it for you.</p>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`ai-designer__message ai-designer__message--${msg.role}`}>
            <div className="ai-designer__avatar">
              <span className="material-icons" style={{ fontSize: '18px' }}>
                {msg.role === 'user' ? 'person' : 'smart_toy'}
              </span>
            </div>
            <div className="ai-designer__bubble">
              {msg.role === 'assistant' ? renderMessageContent(msg.content) : msg.content}
            </div>
          </div>
        ))}

        {streaming && (
          <div className="ai-designer__message ai-designer__message--assistant">
            <div className="ai-designer__avatar">
              <span className="material-icons" style={{ fontSize: '18px' }}>smart_toy</span>
            </div>
            <div className="ai-designer__bubble">
              {streamingContent ? (
                renderMessageContent(streamingContent)
              ) : (
                <div className="ai-designer__typing">
                  <span className="ai-designer__typing-dot" />
                  <span className="ai-designer__typing-dot" />
                  <span className="ai-designer__typing-dot" />
                </div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="ai-designer__error">{error}</div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="ai-designer__input-area">
        <textarea
          ref={textareaRef}
          className="ai-designer__textarea"
          placeholder={chatBlocked ? 'Connect to Gemini to start chatting' : 'Describe the flow you want to create...'}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            autoResize();
          }}
          onKeyDown={handleKeyDown}
          rows={1}
          disabled={streaming || chatBlocked}
        />
        <button
          className="ai-designer__send-btn"
          onClick={handleSend}
          disabled={!input.trim() || streaming || chatBlocked}
          type="button"
          title="Send message"
        >
          <span className="material-icons">send</span>
        </button>
      </div>
    </div>
  );
}
